'use client';

import { Box, Dialog, SystemIcon, Typography } from '@wishket/design-system';
import {
  CSSProperties,
  PropsWithChildren,
  RefObject,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useClickOutside } from '@wishket/yogokit';

import { BubblePopover, HighlightShareBottomSheet } from '@/shared/Components';
import { useDeviceCheck } from '@/shared/hooks';
import { useDeleteHighlightMutation } from '@/entities/article';
import {
  openLinkedInSharePopup,
  openFacebookSharePopup,
  openTwitterSharePopup,
  isValidElement,
} from '@/shared/utils';

interface HighlightedPopoverProps {
  isOpen: boolean;
  onClickOutside: () => void;
  highlightId: string;
  text: string;
  onMemoOpen: () => void;
  spanRef: RefObject<HTMLSpanElement | null>;
}

const POPOVER_GAP = 8;
const ARROW_HEIGHT = 12;

const HighlightedPopover = forwardRef<
  HTMLDivElement,
  PropsWithChildren<HighlightedPopoverProps>
>(
  (
    {
      children,
      isOpen,
      onClickOutside,
      highlightId,
      text,
      onMemoOpen,
      spanRef,
    },
    ref
  ) => {
    const [linkParams, setLinkParams] = useState({ url: '', title: '' });
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isShareSheetOpen, setIsShareSheetOpen] = useState(false);
    const [popoverPosition, setPopoverPosition] = useState<CSSProperties>({});
    const { isDesktop, isLoading } = useDeviceCheck();
    const popoverRef = useRef<HTMLDivElement>(null);
    const { mutate: deleteHighlight } = useDeleteHighlightMutation();

    const openShareSheet = () => {
      setIsShareSheetOpen(true);
    };

    const closeShareSheet = () => {
      setIsShareSheetOpen(false);
    };

    useClickOutside(popoverRef, onClickOutside);

    const handleDeleteHighlight = () => {
      setIsDialogOpen(false);
      deleteHighlight(
        { highlightId },
        {
          onError: () => {
            alert('새로고침 후 다시 시도해주세요.');
          },
        }
      );
    };

    useEffect(() => {
      setLinkParams({
        url: window.location.href,
        title: `"${text}"`,
      });
    }, [text]);

    useEffect(() => {
      if (spanRef.current) {
        const rect = spanRef.current.getBoundingClientRect();
        const parentElement = spanRef.current.parentElement;

        const getValidParent = (element: Element | null) => {
          if (!element) return;
          let elem = element;

          while (!isValidElement(elem)) {
            if (!elem.parentElement) return;
            elem = elem?.parentElement;
          }

          return elem;
        };
        const validParentElement = getValidParent(spanRef.current);

        const isNotHeader = !validParentElement?.tagName.includes('H');
        const popoverGap = isNotHeader ? POPOVER_GAP : 0;

        const parentRect = validParentElement?.getBoundingClientRect();

        if (parentRect && parentElement) {
          const topPosition =
            rect.top -
            parentRect.top +
            parentElement.scrollTop -
            popoverGap +
            (isDesktop ? 0 : ARROW_HEIGHT);
          const leftPosition =
            rect.left -
            parentRect.left +
            parentElement.scrollLeft +
            rect.width / 2;

          setPopoverPosition({
            top: topPosition,
            left: leftPosition,
            height: rect.height,
          });
        }
      }
    }, [isOpen, spanRef, isDesktop]);

    if (isLoading) return children;

    return (
      <>
        <BubblePopover
          isHighlight
          variant={isDesktop ? 'up' : 'down'}
          ref={popoverRef}
          className="absolute"
          style={popoverPosition}
          content={
            <Box
              as="span"
              className="flex items-center gap-3 rounded-xl bg-w-white px-4 py-2 font-normal shadow-graymedium"
            >
              <button
                className="group flex items-center gap-1.5"
                onClick={() => setIsDialogOpen(true)}
              >
                <SystemIcon
                  name="medium_trash"
                  className="group-hover:text-w-gray-600"
                />
                <Typography
                  variant="body1"
                  className="text-w-gray-900 group-hover:text-w-gray-600"
                >
                  삭제
                </Typography>
              </button>
              <Box as="span" className="h-4 w-px flex-shrink-0 bg-w-gray-200" />
              <button
                className="group flex items-center gap-1.5"
                onClick={onMemoOpen}
              >
                <SystemIcon
                  name="medium_bubble"
                  className="group-hover:text-w-gray-600"
                />
                <Typography
                  variant="body1"
                  className="text-w-gray-900 group-hover:text-w-gray-600"
                >
                  메모
                </Typography>
              </button>
              <Box as="span" className="h-4 w-px flex-shrink-0 bg-w-gray-200" />
              {isDesktop ? (
                <Box as="span" className="flex items-center gap-4">
                  <button onClick={() => openTwitterSharePopup(linkParams)}>
                    <SystemIcon name="social_x" className="size-5" />
                  </button>
                  <button onClick={() => openFacebookSharePopup(linkParams)}>
                    <SystemIcon name="social_facebook" className="size-5" />
                  </button>
                  <button onClick={() => openLinkedInSharePopup(linkParams)}>
                    <SystemIcon name="social_linked_in" className="size-5" />
                  </button>
                </Box>
              ) : (
                <button
                  className="flex items-center gap-1.5"
                  onClick={openShareSheet}
                >
                  <SystemIcon name="medium_share" />
                  <Typography variant="body1">형광펜 공유</Typography>
                </button>
              )}
            </Box>
          }
          open={isOpen}
        >
          {children}
        </BubblePopover>
        {isDesktop || (
          <HighlightShareBottomSheet
            url={linkParams.url}
            title={linkParams.title}
            isShareSheetOpen={isShareSheetOpen}
            closeShareSheet={closeShareSheet}
          />
        )}
        <Dialog
          variant="caution"
          title="형광펜 삭제하기"
          description={`형광펜과 메모가 함께 삭제됩니다.\n선택한 형광펜을 삭제할까요?`}
          close="취소"
          confirm="삭제하기"
          isOpen={isDialogOpen}
          onConfirm={handleDeleteHighlight}
          onClose={() => setIsDialogOpen(false)}
        />
      </>
    );
  }
);

HighlightedPopover.displayName = 'HighlightedPopover';

export default HighlightedPopover;
